import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getGridArea } from '../constants/topology';
import { cn } from '../utils/cn';
import { useSettings } from '../contexts/SettingsContext';

const getRainbowColor = (type) => {
  const map = {
    1: {
      center: 'bg-rose-300 text-rose-900 dark:bg-rose-600 dark:text-white border-rose-400 dark:border-rose-500',
      light: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/25 dark:text-rose-200 dark:border-rose-800/30'
    },
    2: {
      center: 'bg-orange-300 text-orange-900 dark:bg-orange-600 dark:text-white border-orange-400 dark:border-orange-500',
      light: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/25 dark:text-orange-200 dark:border-orange-800/30'
    },
    3: {
      center: 'bg-amber-200 text-amber-900 dark:bg-amber-500 dark:text-white border-amber-300 dark:border-amber-400',
      light: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/25 dark:text-amber-200 dark:border-amber-800/30'
    },
    4: {
      center: 'bg-emerald-300 text-emerald-900 dark:bg-emerald-600 dark:text-white border-emerald-400 dark:border-emerald-500',
      light: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-200 dark:border-emerald-800/30'
    },
    5: {
      center: 'bg-sky-300 text-sky-900 dark:bg-sky-600 dark:text-white border-sky-400 dark:border-sky-500',
      light: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/25 dark:text-sky-200 dark:border-sky-800/30'
    },
    6: {
      center: 'bg-indigo-300 text-indigo-900 dark:bg-indigo-600 dark:text-white border-indigo-400 dark:border-indigo-500',
      light: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/25 dark:text-indigo-200 dark:border-indigo-800/30'
    },
    7: {
      center: 'bg-purple-300 text-purple-900 dark:bg-purple-600 dark:text-white border-purple-400 dark:border-purple-500',
      light: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/25 dark:text-purple-200 dark:border-purple-800/30'
    },
    8: {
      center: 'bg-fuchsia-300 text-fuchsia-900 dark:bg-fuchsia-600 dark:text-white border-fuchsia-400 dark:border-fuchsia-500',
      light: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/25 dark:text-fuchsia-200 dark:border-fuchsia-800/30'
    },
  };
  return map[type] || {
    center: 'bg-gray-300 text-gray-900 dark:bg-gray-600 dark:text-white border-gray-400 dark:border-gray-500',
    light: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900/25 dark:text-gray-200 dark:border-gray-800/30'
  };
};

const Cell = ({
  cell, blockType, isZoomed, onChange, readOnly = false,
  isBeingDragged = false, isDropTarget = false,
  onDragHandleDown
}) => {
  const { theme } = useSettings();
  const gridArea = getGridArea(cell.is_center, cell.priority_index);
  const isCoreBlock = blockType === 'CORE';

  const getBackgroundColor = () => {
    if (theme === 'theme-rainbow') {
      if (isCoreBlock) {
        if (cell.is_center) return 'bg-gray-100 dark:bg-gray-200 text-gray-800 dark:text-gray-700 border-gray-300 dark:border-gray-300 font-bold';
        const colors = getRainbowColor(cell.priority_index);
        return `${colors.center}`;
      } else {
        const typeMatch = blockType.match(/BLOCK_(\d)/);
        const type = typeMatch ? parseInt(typeMatch[1], 10) : 1;
        const colors = getRainbowColor(type);
        if (cell.is_center) return `${colors.center} font-bold`;
        return `${colors.light} hover:border-gray-400 dark:hover:border-gray-500`;
      }
    } else {
      if (isCoreBlock) {
        if (cell.is_center) return 'bg-primary-300/85 dark:bg-primary-700/75 text-primary-900 dark:text-primary-50 border-primary-400/70 dark:border-primary-600 font-bold shadow-sm';
        return 'backdrop-blur-xl bg-primary-500/10 dark:bg-primary-400/10 text-primary-900 dark:text-primary-100 border-primary-500/25 dark:border-primary-400/30 hover:border-primary-500/50';
      } else {
        if (cell.is_center) return 'bg-primary-200/85 dark:bg-primary-700/70 text-primary-900 dark:text-primary-50 border-primary-300/80 dark:border-primary-600 font-bold shadow-sm';
        return 'backdrop-blur-xl bg-white/70 dark:bg-white/5 text-gray-900 dark:text-white border-white/60 dark:border-white/10 hover:border-primary-400/60 dark:hover:border-primary-500/50';
      }
    }
  };

  const handleDragHandlePointerDown = (e) => {
    if (cell.is_center) return;
    e.preventDefault();
    e.stopPropagation();
    if (onDragHandleDown) onDragHandleDown(e, cell);
  };

  return (
    <motion.div
      layout
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{ gridArea }}
      data-cell-id={cell.id}
      data-is-center={cell.is_center ? 'true' : 'false'}
      className={cn(
        "group relative rounded-lg border shadow-sm flex items-center justify-center overflow-hidden transition-all outline-none",
        isZoomed ? "p-3 md:p-4" : "p-1 md:p-2",
        getBackgroundColor(),
        isBeingDragged && "opacity-30 scale-95 ring-2 ring-dashed ring-primary-400/50",
        isDropTarget && !cell.is_center && "ring-2 ring-primary-500 ring-offset-2 scale-[1.04] brightness-110"
      )}
    >
      {!cell.is_center && isZoomed && (
        <>
          <span className="absolute top-1.5 left-2 text-[10px] opacity-40 font-mono select-none pointer-events-none">
            {cell.priority_index}
          </span>
          <div
            className="absolute top-0.5 right-0.5 w-7 h-7 flex items-center justify-center rounded-md bg-black/5 dark:bg-white/10 text-black/40 dark:text-white/50 hover:bg-primary-500/20 dark:hover:bg-primary-500/30 hover:text-primary-700 dark:hover:text-primary-300 cursor-grab active:cursor-grabbing z-20 touch-none select-none transition-colors"
            onPointerDown={handleDragHandlePointerDown}
            title="ドラッグして順位を入れ替え"
          >
            <SafeIcon icon={FiIcons.FiMove} className="text-xs" />
          </div>
        </>
      )}
      <textarea
        value={cell.content || ''}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={cn(
          "w-full h-full resize-none bg-transparent text-center focus:outline-none text-inherit",
          isZoomed ? "text-sm sm:text-base leading-snug" : "text-[7px] sm:text-xs leading-tight pointer-events-none cursor-pointer",
          "flex items-center"
        )}
        style={{ display: 'flex', alignItems: 'center' }}
        placeholder={isZoomed ? (cell.is_center ? '中心テーマ' : '要素を入力...') : ''}
      />
    </motion.div>
  );
};

export default Cell;